/* ===================== Go Live (Astrologer) =====================
   The astrologer's own live-streaming screen: start a broadcast on the
   spot, see the one that is running, and read back the ones that are
   over.

   What this is NOT: a live class. There is no schedule, no course and no
   enrollment — the astrologer presses one button and is on air, open to
   the whole site. Anyone can watch without an account; only signed-in
   viewers can type in the chat (enforced by the API, not here).

   Where the camera is: not on this tab. The dashboard is hosted by the
   website app, and the video SDKs live in the jyotish app, so starting a
   stream hands off to its broadcast studio (appUrl('jyotish', '/live/:id'))
   — the same pattern the Appointments tab uses to open a consultation
   room. This screen is the entry point and the history.

   Resuming: POST /live-streams while already live is a success, not an
   error — the server hands back a fresh host token for the running
   stream. So "Back to my broadcast" is the same call as "Go Live", which
   is what makes a reload or a crash recoverable. */
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@astro/api'
import { useAuth } from '@astro/auth'
import { appUrl } from '@astro/config'
import { LuRadio, LuUsers, LuVideo } from 'react-icons/lu'

const MAX_TITLE = 255

function rowsFrom(res) {
  const data = res?.data
  if (Array.isArray(data)) return data
  if (Array.isArray(data?.data)) return data.data
  return []
}

function dateLabel(iso) {
  const d = new Date(iso)
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}

function durationLabel(stream) {
  const start = new Date(stream.started_at).getTime()
  const end = stream.ended_at ? new Date(stream.ended_at).getTime() : Date.now()
  if (!Number.isFinite(start)) return '—'
  const minutes = Math.max(0, Math.round((end - start) / 60000))
  return minutes < 60 ? `${minutes}m` : `${Math.floor(minutes / 60)}h ${minutes % 60}m`
}

/** The jyotish app's broadcast studio for one stream. */
const studioUrl = (id) => appUrl('jyotish', `/live/${id}`)

export default function GoLiveTab() {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [chatEnabled, setChatEnabled] = useState(true)
  const [error, setError] = useState(null)

  const query = useQuery({
    queryKey: ['dashboard', 'my-live-streams'],
    queryFn: () => api.liveStreams.mine({ per_page: 20 }),
    enabled: Boolean(user),
    retry: false,
    // While a broadcast is running the viewer count on this card should move.
    refetchInterval: (q) => (rowsFrom(q.state.data).some((s) => s.is_live) ? 15_000 : false),
  })

  const streams = rowsFrom(query.data)
  const running = streams.find((s) => s.is_live) ?? null
  const past = streams.filter((s) => !s.is_live)

  const goLive = useMutation({
    mutationFn: () =>
      api.liveStreams.goLive({
        title: title.trim(),
        description: description.trim() || undefined,
        chat_enabled: chatEnabled,
      }),
    onSuccess: (res) => {
      const id = res?.data?.live_stream?.id
      if (!id) {
        setError('The stream started but the server did not say which one. Reload and try again.')
        return
      }
      // Full page load: the studio lives in another app on another origin.
      window.location.href = studioUrl(id)
    },
    onError: (err) => setError(err?.message || 'Could not start the stream. Please try again.'),
  })

  const end = useMutation({
    mutationFn: (id) => api.liveStreams.end(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['dashboard', 'my-live-streams'] }),
    onError: (err) => setError(err?.message || 'Could not end the stream.'),
  })

  // 404 here means "this account has no astrologer profile" — the API's own
  // convention for the astrologer endpoints. The tab is astrologer-only, so
  // this is only reachable if a profile was removed under them.
  if (query.isError && query.error?.status === 404) {
    return (
      <div className="bg-white p-6 border border-stone-100 shadow-sm rounded-2xl">
        <h2 className="text-lg font-bold text-stone-900 font-royal">Live streaming</h2>
        <p className="text-sm text-stone-500 mt-2">
          Live streaming is for astrologers. We could not find an astrologer profile on this account.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="rounded-2xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      )}

      {running ? (
        /* ── On air: the one thing that matters is getting back to the studio ── */
        <div className="bg-white p-4 sm:p-6 border border-stone-100 shadow-sm rounded-2xl">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-red-600">
                <LuRadio className="w-3.5 h-3.5 animate-pulse" /> On air
              </span>
              <h2 className="text-lg sm:text-xl font-bold text-stone-900 font-royal mt-1">{running.title}</h2>
              <p className="text-xs text-stone-500 mt-1">
                Started {dateLabel(running.started_at)} · running {durationLabel(running)}
              </p>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <span className="inline-flex items-center gap-1.5 rounded-xl bg-stone-50 px-3 py-2 text-sm font-bold text-stone-800">
                <LuUsers className="w-4 h-4 text-stone-400" />
                {running.viewer_count}
                <span className="text-xs font-medium text-stone-500">watching</span>
              </span>
              <a
                href={studioUrl(running.id)}
                className="inline-flex items-center gap-2 rounded-xl bg-[#C8922A] px-4 py-2.5 text-sm font-bold text-white hover:bg-[#b07f22]"
              >
                <LuVideo className="w-4 h-4" /> Back to my broadcast
              </a>
              <button
                type="button"
                onClick={() => end.mutate(running.id)}
                disabled={end.isPending}
                className="rounded-xl border border-red-200 px-4 py-2.5 text-sm font-semibold text-red-600 hover:bg-red-50 disabled:opacity-50"
              >
                {end.isPending ? 'Ending…' : 'End stream'}
              </button>
            </div>
          </div>
          <p className="text-[11px] text-stone-400 mt-4">
            Your stream is listed publicly while it runs. Anyone can watch; only signed-in viewers can chat.
          </p>
        </div>
      ) : (
        /* ── Not live: the start form ── */
        <div className="bg-white p-4 sm:p-6 border border-stone-100 shadow-sm rounded-2xl">
          <h2 className="text-lg sm:text-xl font-bold text-stone-900 font-royal">Go live</h2>
          <p className="text-sm text-stone-500 mt-1">
            Start an instant session. It appears on the public Live page the moment you begin — there is nothing to schedule
            and no one to invite.
          </p>
          <form
            className="mt-5 space-y-4"
            onSubmit={(e) => {
              e.preventDefault()
              setError(null)
              if (!title.trim()) {
                setError('Give your session a title so people know what it is about.')
                return
              }
              goLive.mutate()
            }}
          >
            <label className="block">
              <span className="text-xs font-bold text-stone-600">Session title *</span>
              <input
                value={title}
                maxLength={MAX_TITLE}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Evening Q&A on the Saturn transit"
                className="mt-1.5 w-full rounded-xl border border-stone-200 px-3.5 py-2.5 text-sm text-stone-800 outline-none focus:border-[#C8922A]"
              />
            </label>
            <label className="block">
              <span className="text-xs font-bold text-stone-600">What will you cover? (optional)</span>
              <textarea
                value={description}
                rows={3}
                maxLength={2000}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="A line or two for the Live page."
                className="mt-1.5 w-full rounded-xl border border-stone-200 px-3.5 py-2.5 text-sm text-stone-800 outline-none focus:border-[#C8922A]"
              />
            </label>
            <label className="flex items-center gap-2.5">
              <input
                type="checkbox"
                checked={chatEnabled}
                onChange={(e) => setChatEnabled(e.target.checked)}
                className="h-4 w-4 accent-[#C8922A]"
              />
              <span className="text-sm text-stone-700">
                Allow viewer chat <span className="text-stone-400">(signed-in viewers only)</span>
              </span>
            </label>
            <button
              type="submit"
              disabled={goLive.isPending}
              className="inline-flex items-center gap-2 rounded-xl bg-[#C8922A] px-5 py-3 text-sm font-bold text-white hover:bg-[#b07f22] disabled:opacity-50"
            >
              <LuRadio className="w-4 h-4" />
              {goLive.isPending ? 'Starting…' : 'Go live now'}
            </button>
            <p className="text-[11px] text-stone-400">
              Your camera and microphone are asked for in the next screen, not here.
            </p>
          </form>
        </div>
      )}

      {/* ── Past broadcasts ── */}
      <div className="bg-white p-4 sm:p-6 border border-stone-100 shadow-sm rounded-2xl">
        <h3 className="text-xs font-bold uppercase tracking-wider text-stone-500">Past broadcasts</h3>
        {query.isPending ? (
          <div className="mt-4 space-y-2 animate-pulse" aria-busy="true">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-9 bg-stone-50 rounded-lg" />
            ))}
          </div>
        ) : past.length === 0 ? (
          <p className="text-sm text-stone-400 mt-3">You have not streamed yet.</p>
        ) : (
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-stone-400">
                  <th className="pb-2 pr-4 font-bold">Session</th>
                  <th className="pb-2 pr-4 font-bold">Started</th>
                  <th className="pb-2 pr-4 font-bold">Ran for</th>
                  <th className="pb-2 pr-4 font-bold">Peak viewers</th>
                  <th className="pb-2 font-bold">Total joins</th>
                </tr>
              </thead>
              <tbody>
                {past.map((s) => (
                  <tr key={s.id} className="border-t border-stone-100">
                    <td className="py-2.5 pr-4 font-semibold text-stone-800">{s.title}</td>
                    <td className="py-2.5 pr-4 text-stone-500">{dateLabel(s.started_at)}</td>
                    <td className="py-2.5 pr-4 text-stone-500">{durationLabel(s)}</td>
                    <td className="py-2.5 pr-4 text-stone-800">{s.peak_viewers}</td>
                    <td className="py-2.5 text-stone-800">{s.total_viewers}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="text-[11px] text-stone-400 mt-3">
              Sessions are not recorded, so there is no replay — these are the numbers they drew.
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
